import React, { useEffect, useRef, useState, useCallback } from 'react';
import CampaignItem from './CampaignItem';

interface MobileHotCampaignsProps {
  hotCampaigns: any[];
  t: (key: string) => string;
}

const MobileHotCampaigns: React.FC<MobileHotCampaignsProps> = ({ hotCampaigns, t }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [isMobile, setIsMobile] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Detect if we're on mobile or web
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollToIndex = useCallback((index: number) => {
    if (!scrollRef.current) return;
    
    const container = scrollRef.current;
    const containerWidth = container.clientWidth;
    
    let itemWidth, gap, totalItemWidth, scrollLeft;
    
    if (isMobile) {
      // Mobile: 70% width with gaps
      itemWidth = containerWidth * 0.7;
      gap = containerWidth * 0.05;
      totalItemWidth = itemWidth + gap;
      scrollLeft = index * totalItemWidth - (containerWidth - itemWidth) / 2;
    } else {
      // Web: 320px cards with 2rem gaps
      itemWidth = 320;
      gap = 2 * 16; // 2rem gap
      totalItemWidth = itemWidth + gap;
      scrollLeft = index * totalItemWidth;
    }
    
    container.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: 'smooth'
    });
  }, [isMobile]);

  const nextSlide = useCallback(() => {
    if (hotCampaigns.length === 0) return;
    
    const nextIndex = (currentIndex + 1) % hotCampaigns.length;
    setCurrentIndex(nextIndex);
    scrollToIndex(nextIndex);
  }, [currentIndex, hotCampaigns.length, scrollToIndex]);

  // Auto-scroll functionality
  useEffect(() => {
    if (!isAutoScrolling || isUserInteracting || hotCampaigns.length <= 1) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(nextSlide, 2000); // Every 2 seconds

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAutoScrolling, isUserInteracting, nextSlide, hotCampaigns.length]);

  // Handle user interaction
  const handleUserInteraction = useCallback(() => {
    setIsUserInteracting(true);
    setIsAutoScrolling(false);
    
    // Resume auto-scroll after 5 seconds of inactivity
    setTimeout(() => {
      setIsUserInteracting(false);
      setIsAutoScrolling(true);
    }, 5000);
  }, []);

  // Handle scroll events to detect current centered item
  const handleScroll = useCallback(() => {
    if (!scrollRef.current || isUserInteracting) return;
    
    const container = scrollRef.current;
    const containerWidth = container.clientWidth;
    const scrollLeft = container.scrollLeft;
    
    let itemWidth, gap, totalItemWidth, centerPosition, newIndex;
    
    if (isMobile) {
      // Mobile calculation
      itemWidth = containerWidth * 0.7;
      gap = containerWidth * 0.05;
      totalItemWidth = itemWidth + gap;
      centerPosition = scrollLeft + containerWidth / 2;
      newIndex = Math.round(centerPosition / totalItemWidth);
    } else {
      // Web calculation - 320px cards with 2rem gaps
      itemWidth = 320;
      gap = 2 * 16; // 2rem gap
      totalItemWidth = itemWidth + gap;
      centerPosition = scrollLeft + containerWidth / 2;
      newIndex = Math.round(centerPosition / totalItemWidth);
    }
    
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < hotCampaigns.length) {
      setCurrentIndex(newIndex);
    }
  }, [currentIndex, hotCampaigns.length, isUserInteracting, isMobile]);

  const handleTouchStart = useCallback(() => {
    handleUserInteraction();
  }, [handleUserInteraction]);

  const handleClick = useCallback(() => {
    handleUserInteraction();
  }, [handleUserInteraction]);

  // Handle mouse events for web manual scrolling
  const handleMouseDown = useCallback(() => {
    handleUserInteraction();
  }, [handleUserInteraction]);

  const handleMouseMove = useCallback(() => {
    handleUserInteraction();
  }, [handleUserInteraction]);

  if (hotCampaigns.length === 0) {
    return null;
  }

  // Calculate pages for dots based on visible items
  const getPageInfo = () => {
    if (!isMobile) return { pageCount: 0, currentPage: 0 }; // No dots for web
    
    // On mobile, show one campaign at a time, so pages = total campaigns
    const itemsPerPage = 1;
    const pageCount = Math.ceil(hotCampaigns.length / itemsPerPage);
    const currentPage = Math.floor(currentIndex / itemsPerPage);
    
    return { pageCount, currentPage };
  };

  const { pageCount, currentPage } = getPageInfo();

  return (
    <div className="mobile-hot-campaigns">
      <div className="mobile-campaigns-title">{t('#Home.hot_campaigns')}</div>
      <div 
        ref={scrollRef}
        className="mobile-campaigns-scroll"
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onClick={handleClick}
      >
        {hotCampaigns.map((item, index) => (
          <div 
            key={index} 
            className={`mobile-campaign-item ${index === currentIndex ? 'active' : ''}`}
          >
            <CampaignItem item={item} />
          </div>
        ))}
      </div>
      
      {/* Dots indicator - Only show on mobile with proper page count */}
      {isMobile && pageCount > 1 && (
        <div className="mobile-campaigns-dots">
          {Array.from({ length: pageCount }, (_, pageIndex) => (
            <div
              key={pageIndex}
              className={`dot ${pageIndex === currentPage ? 'active' : ''}`}
              onClick={() => {
                const targetIndex = pageIndex; // Since itemsPerPage = 1, pageIndex = targetIndex
                setCurrentIndex(targetIndex);
                scrollToIndex(targetIndex);
                handleUserInteraction();
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileHotCampaigns;